import { AppNode, GroupNode } from "@features/builder/graph/types"
import { EditorGraphDto, SocketNodeDto } from "@features/builder/types"
import { convertReactFlowNodeToSocketNode } from "@features/builder/graph/editGraphSocketConverter"
import { GraphConverter } from "@store/services/converter/graphConverter"
import {
  BaseGraphMetaZipNode,
  BaseGraphZip,
  BaseGraphZipMeta,
  BaseGraphZipXY,
} from "@store/services/converter/types"
import { CheckExpressionRequestDto } from "@store/services/editorService"

/**
 * Packs node expression data into a tkrisk file format for backend validation.
 *
 * @param expression - The expression string to validate
 * @param node - The current node being edited
 * @param allNodes - All nodes in the graph (for parent resolution)
 * @param graphDocumentData - The graph document metadata
 * @returns CheckExpressionRequestDto
 */
export const packExpressionRiskFile = async (
  expression: string,
  node: AppNode,
  allNodes: AppNode[],
  graphDocumentData: EditorGraphDto,
): Promise<CheckExpressionRequestDto> => {
  const newNode = structuredClone(node)
  const nodeMap: { [key: string]: SocketNodeDto } = {}
  // replace expression parents and move all parents to map
  if ("originalNode" in newNode.data) {
    newNode.data.originalNode.properties.parents_lst.forEach((id: string) => {
      const found = allNodes.find((n) => n.id === id)
      if (!found) return
      nodeMap[id] = convertReactFlowNodeToSocketNode(found as Exclude<AppNode, GroupNode>)
      if ("metaNode" in found.data)
        expression = expression.replaceAll(`{${found.data.metaNode.label}}`, `{${id}}`)
    })
  }
  if ("originalNode" in newNode.data && newNode.data.originalNode.properties.df_cpd.expression)
    newNode.data.originalNode.properties.df_cpd.expression[0] = expression

  // add edited node to map
  nodeMap[newNode.id] = convertReactFlowNodeToSocketNode(newNode as Exclude<AppNode, GroupNode>)

  // create fake tkrisk file with those nodes
  const graphData = JSON.parse(graphDocumentData.data)
  const tk: { xy?: BaseGraphZipXY; meta?: BaseGraphZipMeta; graph?: BaseGraphZip } = {}
  const xy: { [key: string]: { x: number; y: number } } = {}
  const graph: BaseGraphZip = { nodes: [], attributes: [] }
  const meta = graphData
  meta.fileName = "graph.tkrisk"
  const nodes: { [key: string]: BaseGraphMetaZipNode } = {}
  Object.entries(nodeMap).forEach(([key, value]) => {
    xy[key] = { x: 0, y: 0 }
    nodes[key] = value.data.meta
    if ("node" in value.data) {
      // FIXME: ms this is workaround for faulty backend that expects node to have id instad of using label
      // @ts-ignore
      value.data.node.id = key
      graph["nodes"].push(value.data.node)
    } else if ("attribute" in value.data) {
      // FIXME: ms this is workaround for faulty backend that expects node to have id instad of using label
      // @ts-ignore
      value.data.attribute.id = key
      graph["attributes"]?.push(value.data.attribute)
    }
  })
  meta["nodes"] = nodes
  tk.xy = { coordinates: xy }
  tk.meta = meta
  tk.graph = graph
  const blob = await GraphConverter.packZip(tk)
  return { graphData: blob, nodeId: newNode.id }
}
